import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Logo } from '../components/Logo'
import { ApiError, api } from '../lib/api'
import { Field, inputClass } from './SignIn'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-dpl-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-1.5">
          <Logo size={52} tone="dark" />
          <p className="text-xs text-dpl-muted">Business Management Platform</p>
        </div>
        <div className="rounded-lg border border-dpl-border bg-dpl-surface p-6 shadow-sm">
          <h1 className="mb-3 text-base font-semibold text-dpl-text">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  )
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="mb-4 rounded border border-dpl-danger/30 bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger">
      {children}
    </p>
  )
}

const submitClass =
  'mt-2 w-full rounded-md bg-dpl-primary py-2.5 text-sm font-medium text-white transition hover:bg-dpl-primary-hover disabled:opacity-60'

// Plain anchors, not router links: these pages render outside any router, and a full
// load back to "/" is what should happen after a password changes anyway.
const linkClass = 'text-dpl-primary hover:underline'

const forgotSchema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That does not look like an email address'),
})

/** "Forgot my password": ask for a link. The reply never says whether the address has an account. */
export function ForgotPassword() {
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof forgotSchema>>({ resolver: zodResolver(forgotSchema) })

  const onSubmit = async ({ email }: { email: string }) => {
    setError(null)
    try {
      await api('/api/auth/forgot-password', { method: 'POST', body: { email }, anonymous: true })
      setSentTo(email)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : 'Something went wrong. Try again in a moment.',
      )
    }
  }

  if (sentTo) {
    return (
      <Card title="Check your email">
        <p className="mb-3 text-sm text-dpl-text">
          If <strong>{sentTo}</strong> belongs to an account here, a link to reset its password is on its way.
          It works once, for 30 minutes.
        </p>
        <p className="mb-4 text-sm text-dpl-muted">
          Nothing after a few minutes? Check your spam folder. If this address has no inbox you can read, ask an
          administrator to issue you a temporary password instead.
        </p>
        <a href="/" className={`text-sm ${linkClass}`}>Back to sign in</a>
      </Card>
    )
  }

  return (
    <Card title="Forgot your password?">
      <p className="mb-4 text-sm text-dpl-muted">Enter the email address you sign in with and we will send you a link to choose a new password.</p>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && <Alert>{error}</Alert>}
        <Field label="Email address" error={errors.email?.message}>
          <input {...register('email')} type="email" autoComplete="username" autoFocus className={inputClass(!!errors.email)} />
        </Field>
        <button type="submit" disabled={isSubmitting} className={submitClass}>
          {isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      <p className="mt-4 text-xs">
        <a href="/" className={linkClass}>Back to sign in</a>
      </p>
    </Card>
  )
}

const resetSchema = z
  .object({
    // Mirrors the server's DPL__Auth__MinimumPasswordLength, as ChangePassword does.
    newPassword: z.string().min(10, 'Use at least 10 characters'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  })

/** The token rides in the fragment (#token=…), which the browser never sends to a server. */
function tokenFromLocation(): string | null {
  return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')
}

/** Following the emailed link: choose a new password. */
export function ResetPassword() {
  const [token] = useState(tokenFromLocation)
  const [state, setState] = useState<'form' | 'done' | 'dead'>(token ? 'form' : 'dead')
  const [error, setError] = useState<string | null>(null)

  // Out of the address bar and the history once read: a key to the account should
  // not sit where the next person at this computer can press Back to it.
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])

  const {
    register,
    handleSubmit,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof resetSchema>>({ resolver: zodResolver(resetSchema) })

  const onSubmit = async ({ newPassword }: { newPassword: string }) => {
    setError(null)
    try {
      await api('/api/auth/reset-password', { method: 'POST', body: { token, newPassword }, anonymous: true })
      setState('done')
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError('Too many attempts. Wait a minute and try again.')
      else if (err instanceof ApiError && err.title === 'Password too short') setFieldError('newPassword', { message: err.detail ?? 'Too short' })
      else if (err instanceof ApiError && err.status === 400) setState('dead')
      else setError('Something went wrong. Try again in a moment.')
    }
  }

  if (state === 'done') {
    return (
      <Card title="Password changed">
        <p className="mb-4 text-sm text-dpl-text">
          Your new password is set, and anywhere you were signed in has been signed out. If you use an authenticator
          app, you will still need its code.
        </p>
        <a href="/" className={`text-sm ${linkClass}`}>Sign in</a>
      </Card>
    )
  }

  if (state === 'dead') {
    return (
      <Card title="This link no longer works">
        <p className="mb-4 text-sm text-dpl-text">
          Reset links work once and only for 30 minutes, and asking for a new one cancels the old. Ask for a fresh link and
          use the newest email.
        </p>
        <a href="/forgot-password" className={`text-sm ${linkClass}`}>Send me a new link</a>
      </Card>
    )
  }

  return (
    <Card title="Choose a new password">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && <Alert>{error}</Alert>}
        <Field label="New password" error={errors.newPassword?.message}>
          <input {...register('newPassword')} type="password" autoComplete="new-password" autoFocus className={inputClass(!!errors.newPassword)} />
        </Field>
        <Field label="Confirm new password" error={errors.confirmPassword?.message}>
          <input {...register('confirmPassword')} type="password" autoComplete="new-password" className={inputClass(!!errors.confirmPassword)} />
        </Field>
        <button type="submit" disabled={isSubmitting} className={submitClass}>
          {isSubmitting ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </Card>
  )
}
